import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Loading, Message, Page } from "../components/Page";
import { ApiError, api } from "../lib/api";
import { versionFromParam } from "../lib/copy";
import type { CampaignLink, TakeView } from "../lib/types";
import { ResumeCard, StartForm, StartIntro } from "./Start";

/**
 * /go/:slug: campaign and event links (the QR target). The API says whether the link can
 * start a take right now; for an event series the clock picks before or after.
 */
export function Go() {
  const { slug = "" } = useParams();
  const [params] = useSearchParams();
  // /go/{slug}?v=4 opts in to another scoring version; anything that isn't one to three digits is ignored.
  const scoringVersion = versionFromParam(params.get("v"));
  const [link, setLink] = useState<CampaignLink | null>(null);
  const [error, setError] = useState<"not_found" | "failed" | null>(null);
  const [current, setCurrent] = useState<TakeView | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    let live = true;
    setLink(null);
    setError(null);
    api
      .getCampaign(slug)
      .then((l) => live && setLink(l))
      .catch((e) => live && setError(e instanceof ApiError && (e.status === 404 || e.status === 422) ? "not_found" : "failed"));
    // Someone who scans again part-way through can pick up where they left off.
    api
      .currentTake()
      .then((t) => live && setCurrent(t.status === "in_progress" ? t : null))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [slug]);

  if (error === "not_found") {
    return (
      <Message title="This link isn't valid">
        <p>Check you've scanned or copied the whole link.</p>
        <p className="mt-6 text-[15px]">
          <Link to="/">Go to the start</Link>
        </p>
      </Message>
    );
  }
  if (error === "failed") {
    return (
      <Message title="Something went wrong">
        <p>We couldn't load this page. Check your connection and reload.</p>
      </Message>
    );
  }
  if (!link) return <Loading />;

  if (!link.canStart) {
    return (
      <Page>
        <div className="mt-10">
          <p className="eyebrow text-accent-text">Gifts Profile</p>
          <h1 className="heading mt-3 text-[30px] leading-[1.1]">{link.name}</h1>
          <p className="mt-4 text-[17px] leading-snug">{link.message}</p>
        </div>
      </Page>
    );
  }

  return (
    <Page brand="center">
      <StartIntro kicker={link.name} message={link.message} scoringVersion={scoringVersion} />
      {current && !showForm ? (
        <ResumeCard take={current} onStartOver={() => setShowForm(true)} />
      ) : (
        <StartForm campaign={link.slug} scoringVersion={scoringVersion} />
      )}
    </Page>
  );
}
